import { StatCard } from './StatCard'
import { useDashboardSummaryData } from '../hooks/useDashboardSummary'
import { useToast } from '../../../shared/components/toast'
import { handleApiError } from '../../../shared/utils/handleApiError'
import { useEffect, useCallback } from 'react'
import { useCurrencyFormatter } from '../../../shared/utils/currencyUtils'

// Dashboard stats cards showing revenue, orders, profits, and out of stock
export const StatisticsCards = () => {
  const { toast } = useToast()
  const { summary, isLoading, error } = useDashboardSummaryData()
  const { formatPrice } = useCurrencyFormatter()

  // Error handling
  const handleError = useCallback(() => {
    console.log('Failed to load dashboard summary', error)


    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (error && (error as any)?.errorCode !== 1600) {
      console.log('Dashboard summary error:', error)
      const errorMessage = handleApiError(error)
      toast.error('Failed to load dashboard summary', errorMessage)
    }
  }, [error, toast])

  // Show errors
  useEffect(() => {
    handleError()
  }, [handleError])

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
      <StatCard
        title="Revenue"
        value={isLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : formatPrice(summary?.totalRevenue || 0)}
        icon={
          // Credit card icon (teal)
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="2" y="5" width="20" height="14" rx="2" stroke="#0D9488" strokeWidth="2"/>
            <line x1="2" y1="10" x2="22" y2="10" stroke="#0D9488" strokeWidth="2"/>
            <line x1="6" y1="15" x2="10" y2="15" stroke="#0D9488" strokeWidth="2"/>
          </svg>
        }
      />
      
      <StatCard
        title="Orders"
        value={isLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (summary?.totalOrders || 0).toString()}
        icon={
          // Shopping cart icon (dark gray)
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 6H22L19 16H8L6 6Z" stroke="#374151" strokeWidth="2" strokeLinejoin="round"/>
            <path d="M6 6L4 2H2" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="10" cy="20" r="2" stroke="#374151" strokeWidth="2"/>
            <circle cx="18" cy="20" r="2" stroke="#374151" strokeWidth="2"/>
          </svg>
        }
      />
      
      <StatCard
        title="Total Profits"
        value={isLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : summary?.totalProfit == null ? 'N/A' : formatPrice(summary.totalProfit)}
        icon={
          // Trend up arrow icon (green)
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M23 6L13.5 15.5L8.5 10.5L1 18" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M17 6H23V12" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        }
      />
      
      <StatCard
        title="Out of Stock"
        value={isLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (summary?.totalOutOfStock || 0).toString()}
        valueClassName="text-red-500"
        icon={
          // Warning triangle icon (red)
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 22H22L12 2Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="12" y1="9" x2="12" y2="13" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="17" r="1" fill="#EF4444"/>
          </svg>
        }
      />

      <StatCard
        title="Unique Customers"
        value={isLoading ? (
          <div className="flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (summary?.uniqueCustomers || 0).toString()}
        icon={
          // People icon (indigo)
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="9" cy="7" r="3" stroke="#6366F1" strokeWidth="2"/>
            <path d="M3 21v-2a5 5 0 0 1 5-5h2" stroke="#6366F1" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="17" cy="9" r="2.5" stroke="#6366F1" strokeWidth="2"/>
            <path d="M21 21v-1.5a3.5 3.5 0 0 0-3.5-3.5h-1" stroke="#6366F1" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        }
      />
    </div>
  )
}